import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import BackupPanel from "@/components/BackupPanel";
import ScadenzeTipiPanel from "@/components/ScadenzeTipiPanel";

const FIELDS = [
  ["ragione_sociale", "Ragione sociale"], ["partita_iva", "Partita IVA"], ["codice_fiscale", "Codice fiscale"],
  ["indirizzo", "Indirizzo"], ["cap_citta", "CAP e città"], ["telefono", "Telefono"], ["email", "Email"],
  ["pec", "PEC"], ["iban", "IBAN"], ["iva_default", "IVA predefinita % (prefatture)"],
];

export default function Azienda() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const logoRef = useRef();

  useEffect(() => {
    api.get("/azienda").then((r) => setForm(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/azienda", { ...form, iva_default: Number(form.iva_default ?? 22) });
      setForm(data);
      toast.success("Dati azienda salvati");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/azienda/logo", fd);
      setForm((f) => ({ ...f, logo: data.logo }));
      toast.success("Logo caricato");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/azienda/logo");
      setForm((f) => ({ ...f, logo: undefined }));
      toast.success("Logo rimosso");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="azienda-page">
      <h2 className="text-base md:text-lg font-head font-semibold">Dati azienda</h2>
      <p className="text-sm text-slate-600 -mt-4">Intestazione usata nei PDF delle prefatture e nei documenti generati.</p>
      <div className="bg-white border border-slate-200 rounded-lg p-5 flex items-center gap-5" data-testid="azienda-logo-box">
        <div className="h-20 w-40 border border-dashed border-slate-300 rounded-md flex items-center justify-center bg-slate-50 overflow-hidden">
          {form.logo ? <img src={form.logo} alt="Logo azienda" className="max-h-full max-w-full object-contain" data-testid="azienda-logo-preview" /> : <ImageIcon className="h-6 w-6 text-slate-300" />}
        </div>
        <div className="space-y-2">
          <div className="text-sm font-medium">Logo azienda</div>
          <div className="text-xs text-slate-500">PNG o JPG, max 1 MB. Compare in alto a sinistra nei PDF delle prefatture.</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} data-testid="azienda-logo-upload"><Upload className="h-4 w-4 mr-1.5" />{form.logo ? "Sostituisci" : "Carica logo"}</Button>
            {form.logo && <Button variant="ghost" size="sm" className="text-red-600" onClick={removeLogo} data-testid="azienda-logo-remove"><Trash2 className="h-4 w-4 mr-1.5" />Rimuovi</Button>}
          </div>
          <input ref={logoRef} type="file" accept="image/png,image/jpeg" className="hidden" data-testid="azienda-logo-input" onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label]) => (
          <div key={k} className={k === "ragione_sociale" || k === "indirizzo" ? "sm:col-span-2" : ""}>
            <Label className="text-xs">{label}</Label>
            <Input className="mt-1" value={form[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`azienda-${k}`} type={k === "iva_default" ? "number" : "text"} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <Label className="text-xs">Piè di pagina documento (note legali, coordinate, ecc.)</Label>
          <Textarea rows={3} className="mt-1" value={form.piede_documento ?? ""} onChange={(e) => setForm((f) => ({ ...f, piede_documento: e.target.value }))} data-testid="azienda-piede" />
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="azienda-save">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Salva</Button>
      </div>
      <h2 className="text-base md:text-lg font-head font-semibold pt-2">Tipi di scadenza</h2>
      <ScadenzeTipiPanel />
      <h2 className="text-base md:text-lg font-head font-semibold pt-2">Backup</h2>
      <BackupPanel />
    </div>
  );
}
